import {
	Tooltip,
	TooltipTrigger,
	TooltipContent,
} from "@/components/ui/tooltip"
import { ChevronDownIcon } from "lucide-react"
import { Popover } from "radix-ui"
import { DashboardTableSkeleton } from "./dashboard-skeleton"
import {
	useDashboardActive,
	DASHBOARD_REFRESH_MS,
} from "./use-dashboard-polling"
import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import {
	StalledOrdersQuerySchema,
	StalledOrdersResponseSchema,
} from "@optimist/schemas"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { api } from "@/lib/api"
import { queryKeys } from "@/lib/query-keys"
import { formatDateTime } from "@/lib/utils"
import { formatOrderStatusBadgeLabel } from "@/features/orders/order-status"

function formatElapsed(minutes: number): string {
	const days = Math.floor(minutes / 1440)
	const hours = Math.floor((minutes % 1440) / 60)
	return days > 0 ? `${days}d ${hours}h` : `${hours}h ${minutes % 60}m`
}

export function StalledOrdersPanel({ onPreview }: { onPreview: (orderId: string) => void }) {
	const active = useDashboardActive()
	const [filter, setFilter] = useState(() => StalledOrdersQuerySchema.parse({}))
	const [filterOpen, setFilterOpen] = useState(false)
	const [customDuration, setCustomDuration] = useState(false)
	const [inputHours, setInputHours] = useState(String(filter.hours))
	const [validationError, setValidationError] = useState<string | null>(null)
	const query = useQuery({
		queryKey: queryKeys.stalledOrders(filter.hours, filter.page),
		queryFn: async ({ signal }) =>
			StalledOrdersResponseSchema.parse(
				await api.get<unknown>("/dashboard/stalled", {
					params: filter,
					signal,
				}),
			),
		enabled: active,
		staleTime: DASHBOARD_REFRESH_MS,
		refetchInterval: active ? DASHBOARD_REFRESH_MS : false,
		refetchIntervalInBackground: false,
		refetchOnWindowFocus: false,
	})
	const data = query.data
	return (
		<section
			className="overflow-hidden rounded-2xl border border-border bg-card shadow-card"
			aria-labelledby="stalled-orders-title"
		>
			<div className="flex flex-wrap items-center justify-between gap-4 border-b border-border-subtle px-6 py-5">
				<div>
					<h2
						id="stalled-orders-title"
						className="text-section-title font-semibold"
					>
						Orders not progressing
					</h2>
					<p className="mt-1 text-meta text-muted-foreground">
						Active orders in the same order status, longest first.
					</p>
				</div>
				<Popover.Root
					open={filterOpen}
					onOpenChange={(open) => {
						setFilterOpen(open)
						if (open) {
							setCustomDuration(false)
							setInputHours(String(filter.hours))
							setValidationError(null)
						}
					}}
				>
					<Popover.Trigger asChild>
						<Button
							variant="secondary"
							size="sm"
							className="ml-auto"
							aria-label={`Time threshold: over ${filter.hours} hours`}
						>
							Over {filter.hours === 168 ? "7d" : `${filter.hours}h`}{" "}
							<ChevronDownIcon
								aria-hidden="true"
								className="size-4 shrink-0 text-muted-foreground"
							/>
						</Button>
					</Popover.Trigger>
					<Popover.Portal>
						<Popover.Content
							align="end"
							sideOffset={8}
							collisionPadding={16}
							aria-label="Time without progress"
							className="z-50 w-56 rounded-xl border border-border bg-card p-1.5 font-inter shadow-lg outline-none"
						>
							<p className="px-2.5 py-2 text-meta text-muted-foreground">
								Time without progress
							</p>
							<div className="space-y-0.5">
								{[6, 12, 24, 48, 168].map((hours) => (
									<button
										key={hours}
										type="button"
										aria-pressed={filter.hours === hours}
										className="flex w-full items-center justify-between rounded-md px-2.5 py-2 text-body text-foreground-secondary hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
										onClick={() => {
											setFilter({ hours, page: 1 })
											setFilterOpen(false)
										}}
									>
										{hours === 168 ? "7 days" : `${hours} hours`}{" "}
										{filter.hours === hours ? (
											<span
												aria-hidden="true"
												className="text-brand-foreground"
											>
												✓
											</span>
										) : null}
									</button>
								))}
							</div>
							<div className="mt-1 border-t border-border-subtle pt-1">
								{customDuration ? (
									<form
										className="space-y-2 p-2"
										onSubmit={(event) => {
											event.preventDefault()
											const parsed = StalledOrdersQuerySchema.safeParse({
												hours: inputHours,
												page: 1,
											})
											if (!parsed.success) {
												setValidationError("Enter 1–8,760 whole hours.")
												return
											}
											setFilter(parsed.data)
											setFilterOpen(false)
										}}
									>
										<Label htmlFor="stalled-hours">Custom hours</Label>
										<div className="flex gap-2">
											<Input
												id="stalled-hours"
												type="number"
												min={1}
												max={8760}
												step={1}
												required
												value={inputHours}
												onChange={(event) => setInputHours(event.target.value)}
												className="min-w-0 tabular-nums"
												aria-invalid={validationError !== null}
												aria-describedby={
													validationError ? "stalled-filter-error" : undefined
												}
											/>
											<Button size="sm" type="submit" >
												Apply
											</Button>
										</div>
										{validationError ? (
											<p
												id="stalled-filter-error"
												role="alert"
												className="text-meta text-destructive"
											>
												{validationError}
											</p>
										) : null}
									</form>
								) : (
									<button
										type="button"
										className="w-full rounded-md px-2.5 py-2 text-left text-body text-muted-foreground hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
										onClick={() => setCustomDuration(true)}
									>
										Custom duration…
									</button>
								)}
							</div>
						</Popover.Content>
					</Popover.Portal>
				</Popover.Root>
			</div>
			{query.isError ? (
				<div role="alert" className="px-6 py-5 text-body">
					<p>Unable to load orders without progress.</p>
					<Button
						className="mt-3"
						variant="outline"
						size="sm"
						onClick={() => void query.refetch()}
					>
						Retry
					</Button>
				</div>
			) : data === undefined ? (
				<DashboardTableSkeleton stalled />
			) : (
				<>
					{data.orders.length === 0 ? (
						<p className="px-6 py-6 text-body text-muted-foreground">
							No orders with known status history match this threshold.
						</p>
					) : (
						<div className="overflow-x-auto">
							<table className="w-full text-left text-data text-foreground-secondary [&_td]:whitespace-nowrap">
								<thead className="bg-muted text-label uppercase text-subtle-foreground">
									<tr>
										{[
											"Order",
											"Name",
											"Order status",
											"In status since",
											"Time in status",
										].map((label) => (
											<th
												key={label}
												scope="col"
												className="px-6 py-3 font-semibold"
											>
												{label}
											</th>
										))}
									</tr>
								</thead>
								<tbody className="divide-y divide-border-subtle">
									{data.orders.map((order) => (
										<tr
											key={order.id}
											onClick={() => onPreview(order.id)}
											className="cursor-pointer hover:bg-muted focus-within:bg-muted"
										>
											<td className="px-6 py-3">
												<Link
													to="/dashboard/orders/$orderId"
													params={{ orderId: order.id }}
													onClick={(event) => event.stopPropagation()}
													title={order.id}
													aria-label={`View order ${order.id}`}
													className="font-mono text-meta text-brand-foreground underline-offset-4 hover:underline"
												>
													{order.id.slice(0, 8)}…
												</Link>
											</td>
											<td className="px-6 py-3">
												<button
													type="button"
													className="text-left hover:underline focus-visible:outline-ring"
													aria-label={`Preview order ${order.id}`}
													onClick={(event) => {
														event.stopPropagation()
														onPreview(order.id)
													}}
												>
													{order.patientName || "—"}
												</button>
											</td>
											<td className="px-6 py-3">
												{formatOrderStatusBadgeLabel(order.status)}
											</td>
											<td className="whitespace-nowrap px-6 py-3 text-meta tabular-nums">
												{formatDateTime(order.statusSince)}
											</td>
											<td className="whitespace-nowrap px-6 py-3 tabular-nums">
												{formatElapsed(order.elapsedMinutes)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
					{data.unknownHistoryCount > 0 ? (
						<p className="border-t border-border-subtle px-6 py-3 text-meta text-muted-foreground">
							{data.unknownHistoryCount.toLocaleString()} active orders excluded
							because their status history is missing or inconsistent.
						</p>
					) : null}
					<div className="flex flex-wrap items-center justify-between gap-3 border-t border-border-subtle px-6 py-3">
						<div className="flex items-center gap-3 text-meta">
							<span className="text-muted-foreground">
								{data.total.toLocaleString()} orders
							</span>
							<span aria-hidden="true" className="h-3 w-px bg-border" />
							<Tooltip>
								<TooltipTrigger asChild>
									<button
										type="button"
										className="rounded text-subtle-foreground hover:text-foreground-secondary focus-visible:outline-ring"
										aria-label={`Last refreshed ${formatDateTime(data.generatedAt)}`}
									>
										{query.isFetching
											? "Refreshing…"
											: `Updated ${new Date(data.generatedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`}
									</button>
								</TooltipTrigger>
								<TooltipContent>
									Last refreshed {formatDateTime(data.generatedAt)}
								</TooltipContent>
							</Tooltip>
						</div>
						<div className="flex items-center gap-2">
							<Button
								variant="secondary"
								size="sm"
								disabled={query.isFetching || data.page <= 1}
								onClick={() => setFilter({ ...filter, page: data.page - 1 })}
							>
								Previous
							</Button>
							<span
								className="text-meta tabular-nums text-muted-foreground"
								aria-label={`Page ${data.page} of ${Math.max(1, Math.ceil(data.total / data.pageSize))}`}
							>
								{data.page} /{" "}
								{Math.max(1, Math.ceil(data.total / data.pageSize))}
							</span>
							<Button
								variant="secondary"
								size="sm"
								disabled={
									query.isFetching || data.page * data.pageSize >= data.total
								}
								onClick={() => setFilter({ ...filter, page: data.page + 1 })}
							>
								Next
							</Button>
						</div>
					</div>
				</>
			)}
		</section>
	)
}
